import type { ReactNode } from 'react';

import { assetUrl } from '../../../assets/asset-manifest.js';
import { HeroFrame } from '../../../components/index.js';
import { PLATE_LEAD, PLATE_TITLE } from '../../../styles/plate-type.js';

export interface GamesPageHeaderProps {
  actions?: ReactNode;
}

/**
 * The Games page's own header (APP_SPEC §17, §18; approved Games-page mockup)
 * — a shallow parchment hero, deliberately shorter than `ProfileHero` but
 * built from the same materials: the `hero` frame variant (so its brass
 * corners and edge weight are the profile plate's, not a 1px CSS border), the
 * profile plate's Alegreya, and a theme-art layer carrying its own veil rather
 * than an opacity on the panel — the technique `ProfileHero`/
 * `HeroThemeBackdrop` use, so the heading text is never faded along with the
 * illustration.
 *
 * The map art runs **full bleed** across the whole header, which is the one
 * thing that makes it sit in the parchment rather than on it. The illustration
 * is already composed for exactly this placement: its left 45% is empty
 * parchment carrying nothing but a compass rose, and every motif — wizard's
 * tower, scrying orb, cave mouth, library ruin — is drawn into its right half.
 * Confining the layer to a right-hand strip, as this did before, cropped a
 * window *out of* that right half and then scaled it to fit, which is what
 * reduced a 2172×724 illustration to a small mis-scaled patch pinned to the
 * corner. Given the full width, the art's own blank half falls under the
 * heading by the artist's design, so there is no seam to hide and no
 * rectangular edge anywhere.
 *
 * The veil is therefore only a legibility wash over the left third, in the
 * panel's own `parchment` token rather than the `background` token it used to
 * fade to — that mismatch is what read as a washed-out vertical band down the
 * middle of the hero.
 */
export function GamesPageHeader({ actions }: GamesPageHeaderProps) {
  return (
    <HeroFrame as="header" className="relative overflow-hidden rounded-lg bg-parchment shadow-3">
      {/* Full bleed, and anchored above centre: the band a 3:1 illustration
          shows inside a header this shallow is chosen to hold the tower, the
          cave mouth and the scrying orb rather than the empty sky above them. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 hidden bg-cover bg-[position:center_42%] bg-no-repeat opacity-[0.87] [filter:saturate(1.08)_contrast(1.05)] tablet:block"
        style={{ backgroundImage: `url("${assetUrl('gamesHeroBackground')}")` }}
      />
      {/* Legibility wash under the title only, in the panel's own colour, gone
          well before the motifs begin. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 hidden bg-[linear-gradient(90deg,var(--px-color-parchment,#fff7e4)_0%,rgb(255_247_228/0.9)_16%,rgb(255_247_228/0.5)_32%,rgb(255_247_228/0.16)_46%,transparent_60%)] tablet:block"
      />
      {/* The same paper grain every other parchment surface carries, so the
          illustration reads as printed into the sheet rather than pasted on. */}
      <span
        aria-hidden="true"
        className="px-paper-grain pointer-events-none absolute inset-0 hidden opacity-25 mix-blend-multiply tablet:block"
      />

      {/* The band the art gets is set here, by the header's own height, and it
          is the only dimension left to give it. The illustration is 3:1 and the
          header is far wider than that, so a `cover` fit is width-constrained:
          the full horizontal extent of the map is already on screen and cannot
          be widened. What a short header costs is *vertical* extent — at ~120px
          only a ~30% slice of the painting survives, which is why it read as a
          scrap rather than a setting even once it ran full width. This floor
          shows roughly half the plate — the wizard's tower, the cave mouth and
          the scrying orb together — while keeping the header far shallower than
          `ProfileHero`. */}
      <div className="relative z-[1] flex flex-col justify-between gap-4 px-5 py-5 tablet:min-h-[184px] tablet:flex-row tablet:items-center tablet:gap-6 tablet:px-8 tablet:py-7">
        <div className="min-w-0">
          {/* The shared plate `<h1>` treatment — not `.page-title`
              (Cinzel/Palatino display), which is what made the two pages'
              titles read as different type systems. This header used to set
              its own slightly larger scale (2.3/2.6/2.85rem), so the two
              plates' titles ran 2.4-4px apart; both now read the one value. */}
          <h1 className={`${PLATE_TITLE} text-textPrimary`}>Games</h1>
          <p className={`mt-1.5 max-w-readable ${PLATE_LEAD} text-textPrimary/90`}>
            Choose a game, continue a run, or challenge another player.
          </p>
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </HeroFrame>
  );
}
